import { LOGIN } from '@rcm/login';
import { LoadingIndicatorOverlay } from '@rcm/react-utils';
import { InvisibleReCaptcha2Button, useCaptchaActionEnabled } from '@rcm/recaptcha-2';
import axios from 'axios';
import t from 'rcm-i18n';
import React, { useCallback, useEffect, useState } from 'react';
import '../styles/login-form-block.scss';

enum OperationStatus {
  NotStarted = 'NOT_STARTED',
  Loading = 'LOADING',
  ErrorIncorrectUsernameOrPassword = 'ERROR_INCORRECT_USERNAME_OR_PASSWORD',
  ErrorSystemFailure = 'ERROR_SYSTEM_FAILURE',
  ErrorUnauthorizedTryLoggingIn = 'ERROR_UNAUTHORIZED_TRY_LOGGING_IN',
  ErrorDisabledAccount = 'ERROR_DISABLED_ACCOUNT',
  ErrorCaptchaFailed = 'ERROR_CAPTCHA_FAILED',
}

enum Action {
  oidcPrompt = 'oidcPrompt',
}

export const LoginFormBlock = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState('');
  const [operationStatus, setOperationStatus] = useState(OperationStatus.NotStarted);
  const urlSearchParams = new URLSearchParams(window.location.search);

  const isCaptchaEnabled = useCaptchaActionEnabled(LOGIN);

  const urlParams = {
    action: urlSearchParams.get('action'),
    oidcPrompt: urlSearchParams.get('oidcPrompt'),
    error: urlSearchParams.get('error'),
    errorCode: urlSearchParams.get('errorCode'),
    redirect: urlSearchParams.get('redirect'),
    state: urlSearchParams.get('state'),
    oidcInteractionId: urlSearchParams.get('oidcInteractionId'),
  };

  const handleUsernameChange = useCallback((e) => {
    setUsername(e.target.value);
  }, []);
  const handlePasswordChange = useCallback((e) => {
    setPassword(e.target.value);
  }, []);

  const handleLoginSubmit = useCallback(() => {
    setOperationStatus(OperationStatus.Loading);
    (async () => {
      let response;
      try {
        response = await axios.post('/api/session', {
          username,
          password,
          ...(isCaptchaEnabled ? { captcha } : {}),
        });
      } catch (e) {
        if (e.response?.status === 422) {
          window?.grecaptcha?.reset();
          if (e.response?.data.error === OperationStatus.ErrorDisabledAccount) {
            setOperationStatus(OperationStatus.ErrorDisabledAccount);
          } else if (e.response?.data.error === OperationStatus.ErrorIncorrectUsernameOrPassword) {
            setOperationStatus(OperationStatus.ErrorIncorrectUsernameOrPassword);
          } else if (e.response?.data.error === OperationStatus.ErrorCaptchaFailed) {
            setOperationStatus(OperationStatus.ErrorCaptchaFailed);
          } else {
            setOperationStatus(OperationStatus.ErrorSystemFailure);
          }
          return;
        } else {
          setOperationStatus(OperationStatus.ErrorSystemFailure);
          return;
        }
      }
      if (response.data.success === true) {
        if (urlParams.action === Action.oidcPrompt && urlParams.oidcPrompt === 'login') {
          if (!response.data.oidcBaseUrl) {
            window.location.href = '/login?errorCode=systemFailure';
            return;
          }
          let oidcResponse;
          try {
            oidcResponse = await axios.post(
              `${response.data.oidcBaseUrl}/oidc-interaction/` + `${urlParams.oidcInteractionId}/login`,
              {
                username,
                password,
              }
            );
          } catch (e) {
            window.location.href = '/login?errorCode=systemFailure';
            return;
          }
          if (!oidcResponse?.data?.returnToUrl) {
            window.location.href = '/login?errorCode=systemFailure';
            return;
          }
          window.location.href = oidcResponse.data.returnToUrl;
        } else {
          window.location.href = `/login-home?redirect=${encodeURIComponent(urlParams.redirect)}`;
        }
      } else {
        setOperationStatus(OperationStatus.ErrorSystemFailure);
      }
    })();
  }, [
    username,
    password,
    captcha,
    isCaptchaEnabled,
    urlParams.action,
    urlParams.oidcPrompt,
    urlParams.oidcInteractionId,
    urlParams.redirect,
  ]);

  useEffect(() => {
    let errorCode = urlParams.errorCode;
    if (!errorCode && urlParams.state) {
      let state;
      try {
        state = JSON.parse(urlParams.state);
      } catch (e) {
        // If the state param isn't valid JSON, that is ok. This can happen with third party OIDC clients.
      }
      errorCode = state?.errorCode;
    }
    const oldUrlErrorCodeToOperationStatus = {
      unauthorized: OperationStatus.ErrorUnauthorizedTryLoggingIn,
      invalid: OperationStatus.ErrorIncorrectUsernameOrPassword,
      missing: OperationStatus.ErrorIncorrectUsernameOrPassword,
      systemFailure: OperationStatus.ErrorSystemFailure,
    };
    if (errorCode) {
      if (errorCode in oldUrlErrorCodeToOperationStatus) {
        setOperationStatus(oldUrlErrorCodeToOperationStatus[errorCode]);
      } else {
        setOperationStatus(OperationStatus.ErrorSystemFailure);
      }
    }
  }, [urlParams.errorCode, urlParams.state]);

  // return (
  //   <div className="login-form-block">
  //     <p className="alert alert-info">Login has been temporarily disabled</p>
  //   </div>
  // );

  return (
    <div className="login-form-block">
      <h1>{t('Login')}</h1>
      {operationStatus === OperationStatus.ErrorIncorrectUsernameOrPassword && (
        <div className="alert alert-warning">{t('The username or password was incorrect.')}</div>
      )}
      {operationStatus === OperationStatus.ErrorSystemFailure && (
        <div className="alert alert-warning">
          {t('An error occurred. Please ensure you are connected to the internet and try again.')}
        </div>
      )}
      {operationStatus === OperationStatus.ErrorUnauthorizedTryLoggingIn && (
        <div className="alert alert-warning">
          {t('You do not have permission to access that page. Please login and try again.')}
        </div>
      )}
      {operationStatus === OperationStatus.ErrorDisabledAccount && (
        <div className="alert alert-warning">
          {t(
            'Online access to your Reliv account is currently disabled due to a change in your account status. Please call Reliv’s Distributor Services Center at 800 RELIV US (735.4887) to update your account.'
          )}
        </div>
      )}
      {operationStatus === OperationStatus.ErrorCaptchaFailed && (
        <div className="alert alert-warning">{t('CAPTCHA validation failed.')}</div>
      )}
      <div className="loginContent">
        <form className="loginForm rcm-login-form" onSubmit={handleLoginSubmit}>
          <LoadingIndicatorOverlay
            style={{
              maxWidth: '320px',
            }}
            overlay="main"
            loading={operationStatus === OperationStatus.Loading}
          >
            <label data-textedit="userNameCopy" htmlFor="rcmLoginUsername">
              {t('RCN or Username:')}
            </label>
            <br />
            <input
              autoFocus
              tabIndex={1}
              type="text"
              name="username"
              value={username}
              onChange={handleUsernameChange}
            />
            <br />
            <label data-textedit="loginPasswordCopy" htmlFor="rcmLoginPassword">
              {t('Password')}:
            </label>
            <br />
            <input tabIndex={1} type="password" name="password" value={password} onChange={handlePasswordChange} />
            <div className="login-form-submit-container">
              <InvisibleReCaptcha2Button
                action={LOGIN}
                badge="inline"
                btn="primary"
                className="loginButton"
                onValueChange={setCaptcha}
                tabIndex={1}
                text={t('Login')}
                type="submit"
              />
            </div>
          </LoadingIndicatorOverlay>
        </form>
      </div>
      <div className="loginFooter">
        <div data-richedit="bottomLoginText">
          <a href="/forgot-password">{t('Forgot your password?')}</a>
        </div>
      </div>
    </div>
  );
};
